import * as React from "react";
import { ChangeEvent, FunctionComponent, ReactElement, useEffect, useRef, useState } from "react";
import AckLogger from "../common/AckLogger";
import { useForm, UseFormReturn, ValidateResult } from "react-hook-form";
import { FieldErrors } from "react-hook-form/dist/types/errors";
import { RegisterOptions } from "react-hook-form/dist/types/validator";
import { formatCurrency, formatDate, formatDateTime, formatDecimal, formatPercentage } from "../common/AckUI";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/** Form modes for the AckModelForm component. */
export enum FormModes {
  READ,
  WRITE,
}

/** Model option for the AckModelForm component. */
export type ModelOption = {
  key: string;
  label: string;
  value: any;
  checked?: boolean;
}

/** Model specification for the AckModelForm component. */
export type ModelSpec = {
  key: string;
  label: string;
  name: string;
  placeholder?: string;
  type?: string;
  props?: object;
  options?: ModelOption[];
  constraints?: object;
}

/** Model validator for the AckModelForm component. */
export type ModelValidator = (value: any) => string

/** Model validators for the AckModelForm component. */
export type ModelValidators = { [name: string]: ModelValidator[] }

/** Props for the AckModelForm component. */
export type AckModelFormProps = {
  spec: ModelSpec[];
  mode: FormModes;
  value?: object;
  className?: string;
  validators?: ModelValidators;
  onSubmit?: (state: object) => void;
}

/** Props for the AckMultiModelForm component. */
export type AckMultiModelFormProps = {
  size: number;
  spec: ModelSpec[];
  mode: FormModes;
  values?: object[];
  className?: string;
  validators?: ModelValidators;
  onSubmit?: (state: object[]) => void;
}

/** Filters out the id and userId fields from the model spec. */
export const asSpecs = (spec: ModelSpec[]) => spec.filter(model => model.name !== "id" && model.name !== "userId");

/** Converts a value to a display value. */
export const convertToDisplayValue = (spec: ModelSpec, values: any): string | ReactElement | undefined => {
  const value = values?.[spec.name];
  switch (spec.type) {
    case "currency":
      const currency = value == undefined ? formatCurrency(0.0) : formatCurrency(value);
      AckLogger.info("ToDisplay formatted currency", currency);
      return currency;
    case "decimal":
      const decimal = value == undefined ? formatDecimal(0.0) : formatDecimal(value);
      AckLogger.info("ToDisplay formatted decimal", decimal);
      return decimal;
    case "percentage":
      const percentage = value == undefined ? formatPercentage(0.0) : formatPercentage(value);
      AckLogger.info("ToDisplay formatted percentage", percentage);
      return percentage;
    case "date":
      const dateString = value == undefined ? formatDate(new Date().getTime() / 1000) : formatDate(value);
      AckLogger.info("ToDisplay formatted date", dateString);
      return dateString;
    case "datetime-local":
      const datetimeString = value == undefined ? formatDateTime(new Date().getTime() / 1000) : formatDateTime(value);
      AckLogger.info("ToDisplay formatted datetime", datetimeString);
      return datetimeString;
    case "checkbox":
      const selectValues = value == undefined ? new Set() : new Set([value]);
      const selectOptions = spec.options!.filter(o => selectValues.has(o.value)).map(o => o.label);
      // Single selection checkboxes.
      if (spec.options?.length === 1) {
        return selectOptions.length > 0 ?
          (<span className="has-text-primary"><FontAwesomeIcon icon="check"/></span>) :
          (<span className="has-text-danger"><FontAwesomeIcon icon="times"/></span>);
      }
      return `${selectOptions}`;
    case "radio":
    case "select":
      const selectOption = value == undefined ? [] : spec.options!.filter(o => o.value === value);
      return selectOption.length > 0 ? selectOption[0].label : value;
    default:
      return value == undefined ? value : `${value}`;
  }
};

/** Converts a value to an input value. */
const convertToInputValue = (spec: ModelSpec, values: any): ReactElement | string | undefined => {
  switch (spec.type) {
    case "date":
    case "datetime-local":
      return convertToDisplayValue(spec, values);
    default:
      return values?.[spec.name];
  }
};

/** Converts an input value to a value. */
const convertFromInputValue = (spec: ModelSpec, values: any): any => {
  const value = values?.[spec.name];
  if (value == undefined) {
    return undefined;
  }
  switch (spec.type) {
    case "date":
    case "datetime-local":
      const datetime = new Date(`${value}:00Z`).getTime() / 1000;
      AckLogger.info("FromDisplay formatted date/datetime", datetime);
      return datetime;
    default:
      return value;
  }
};

/** Resolves an error message. */
const resolveErrorMessage = (errors: FieldErrors, model: ModelSpec): string | undefined => {
  const error = errors[model.name] || undefined;
  if (error) {
    AckLogger.info("Error for model", model.name, error);
    let errorMessage = `You must provide a valid value for ${model.name}`;
    switch (error?.type) {
      case "required":
        errorMessage = `The ${model.name} field is required`;
        break;
      case "max":
        errorMessage = `The ${model.name} field must be greater than ${model.constraints!["max"]}`;
        break;
      case "min":
        errorMessage = `The ${model.name} field must be lower than ${model.constraints!["min"]}`;
        break;
      case "maxLength":
        errorMessage = `The ${model.name} field must be at most ${model.constraints!["maxLength"]} chars long`;
        break;
      case "minLength":
        errorMessage = `The ${model.name} field must be at least ${model.constraints!["minLength"]} chars long`;
        break;
      case "pattern":
        errorMessage = `The ${model.name} has an invalid format`;
        break;
    }
    return error?.message?.toString() || errorMessage;
  }
  return undefined;
};

/** Converts a model to validation rules. */
const convertToValidationRules = (model: ModelSpec, validators: ModelValidators | undefined): RegisterOptions | undefined => {
  const validations = validators?.[model.name] || [];
  const validateAdapter = (data: any): ValidateResult => {
    return validations.map(v => v(data)).filter(message => message !== undefined);
  };
  const rules = {};
  Object.keys(model.constraints || {}).forEach(key => {
    const constraint = model.constraints?.[key];
    if (constraint) rules[key] = constraint;
  });
  if (validations.length > 0) rules["validate"] = validateAdapter;
  AckLogger.info(`Converted validation rules for [${model.name}] field`, rules);
  return rules;
};

const FormInput = (model: ModelSpec, validators: ModelValidators, form: UseFormReturn, type: string = "text") => {
  return (
    <div key={"form-group-" + model.key} className="field">
      <label className="label" key={"form-label-" + model.key} htmlFor={model.key}>{model.label}</label>
      <div className="control">
        <input
          {...model.props}
          {...form.register(model.name, convertToValidationRules(model, validators))}
          className={form.formState.errors[model.name] ? "input is-danger" : "input"}
          key={model.key}
          type={type}
          name={model.name}
          placeholder={model.placeholder}
        />
        <div className="help is-danger">{resolveErrorMessage(form.formState.errors, model)}</div>
      </div>
    </div>
  );
};

const FormFileInput = (model: ModelSpec, validators: ModelValidators, form: UseFormReturn) => {
  const [filename, setFilename] = useState("No file selected yet");

  const setFileName = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    if (input.files?.length || 0 > 0) {
      setFilename(input.files![0].name);
    }
  };

  return (
    <div key={"form-group-" + model.key} className="field">
      <label className="label" key={"form-label-" + model.key} htmlFor={model.key}>{model.label}</label>
      <div key={"form-group-file-" + model.key} className="file has-name">
        <label className="file-label is-danger">
          <input
            {...model.props}
            {...form.register(model.name, convertToValidationRules(model, validators))}
            className={form.formState.errors[model.name] ? "file-input is-danger" : "file-input"}
            key={model.key}
            type="file"
            name={model.name}
            onChange={setFileName}
            placeholder={model.placeholder}
          />
          <span className="file-cta">
            <span className="file-icon"><FontAwesomeIcon icon="upload"/></span>
            <span className="file-label">Choose a file</span>
          </span>
          <span className="file-name">{filename}</span>
        </label>
      </div>
      <div className="help is-danger">{resolveErrorMessage(form.formState.errors, model)}</div>
    </div>
  );
};

const FormDecimal = (model: ModelSpec, validators: ModelValidators, form: UseFormReturn) => {
  return (
    <div key={"form-group-" + model.key} className="field">
      <label className="label" key={"form-label-" + model.key} htmlFor={model.key}>{model.label}</label>
      <div className="control">
        <input
          {...model.props}
          {...form.register(model.name, convertToValidationRules(model, validators))}
          step="any"
          defaultValue={0.00}
          className={form.formState.errors[model.name] ? "input is-danger" : "input"}
          key={model.key}
          type="number"
          name={model.name}
          placeholder={model.placeholder}
        />
        <div className="help is-danger">{resolveErrorMessage(form.formState.errors, model)}</div>
      </div>
    </div>
  );
};

const FormDate = (model: ModelSpec, validators: ModelValidators, form: UseFormReturn) => {
  return (
    <div key={"form-group-" + model.key} className="field">
      <label className="label" key={"form-label-" + model.key} htmlFor={model.key}>{model.label}</label>
      <div className="control">
        <input
          {...model.props}
          {...form.register(model.name, convertToValidationRules(model, validators))}
          className={form.formState.errors[model.name] ? "input is-danger" : "input"}
          key={model.key}
          type={model.type}
          name={model.name}
          placeholder={model.placeholder}
        />
        <div className="help is-danger">{resolveErrorMessage(form.formState.errors, model)}</div>
      </div>
    </div>
  );
};

const FormRadio = (model: ModelSpec, validators: ModelValidators, form: UseFormReturn) => {
  const className = form.formState.errors[model.name] ? "is-danger" : "";
  const options = model.options!.map(o => {
    return (
      <>
        <label className="radio" key={"form-label-" + model.key + "-" + o.key}>
          <input
            {...model.props}
            {...form.register(model.name, convertToValidationRules(model, validators))}
            className={className}
            type="radio"
            key={"option-" + o.key}
            name={model.name}
            value={o.value}
            defaultChecked={o.checked}
          />
          {" " + o.label}
        </label>
        <br/>
      </>
    );
  });
  return (
    <div key={"form-group-" + model.key} className="field">
      <label className="label" key={"form-label-" + model.key}>{model.label}</label>
      <div className="control">{options}</div>
      <div className="help is-danger">{resolveErrorMessage(form.formState.errors, model)}</div>
    </div>
  );
};

const FormCheckbox = (model: ModelSpec, validators: ModelValidators, form: UseFormReturn) => {
  console.assert(model.options, "Options for checkbox components must be set");
  const error = form.formState.errors[model.name] || undefined;
  const className = error ? "is-danger" : "";
  const options = model.options!.map(o => {
    return (
      <div key={"wrapper-" + o.key}>
        <label className="checkbox" key={"form-label-" + model.key + "-" + o.key}>
          <input
            {...model.props}
            {...form.register(model.name, convertToValidationRules(model, validators))}
            className={className}
            type="checkbox"
            key={"option-" + o.key}
            name={model.name}
            value={o.value}
            defaultChecked={o.checked}
          />
          {" " + o.label}
        </label>
        <br/>
      </div>
    );
  });
  return (
    <div key={"form-group-" + model.key} className="field">
      <label className="label" key={"form-label-" + model.key}>{model.label}</label>
      <div className="control">{options}</div>
      <div className="help is-danger">{resolveErrorMessage(form.formState.errors, model)}</div>
    </div>
  );
};

const FormSelect = (model: ModelSpec, validators: ModelValidators, form: UseFormReturn) => {
  console.assert(model.options, "Options for select components must be set");
  const options = model.options!.map(o => {
    return (<option{...model.props} key={"option-" + o.key} value={o.value}>{o.label}</option>);
  });
  return (
    <div key={"form-group-" + model.key} className="field">
      <label key={"form-label-" + model.key} className="label">{model.label}</label>
      <div key={"form-control-" + model.key} className="control">
        <div className="select is-fullwidth">
          <select
            {...form.register(model.name, convertToValidationRules(model, validators))}
            key={model.key}
            name={model.name}
            className={form.formState.errors[model.name] ? "is-danger" : ""}
          >
            {options}
          </select>
        </div>
      </div>
      <div className="help is-danger">{resolveErrorMessage(form.formState.errors, model)}</div>
    </div>);
};

const renderForm = (specs: ModelSpec[], validators: ModelValidators, form: UseFormReturn) => {
  return asSpecs(specs).map(model => {
    AckLogger.info("Processing spec", model);
    const type = model.type || "text";
    let input;
    if (type == "radio") {
      input = FormRadio(model, validators, form);
    } else if (type == "checkbox") {
      input = FormCheckbox(model, validators, form);
    } else if (type == "select") {
      input = FormSelect(model, validators, form);
    } else if (type == "date" || type == "datetime-local") {
      input = FormDate(model, validators, form);
    } else if (type == "currency") {
      input = FormDecimal(model, validators, form);
    } else if (type == "decimal") {
      input = FormDecimal(model, validators, form);
    } else if (type == "file") {
      input = FormFileInput(model, validators, form);
    } else if (type == "password") {
      input = FormInput(model, validators, form, "password");
    } else {
      input = FormInput(model, validators, form, "text");
    }
    return input;
  });
};

/** AckModelForm component. */
export const AckModelForm: FunctionComponent<AckModelFormProps> = (props) => {
  const form = useForm();
  useEffect(() => {
    const values = Object.fromEntries(props.spec.map(spec => [spec.name, convertToInputValue(spec, props.value)]));
    form.reset(values as any);
  }, [form.reset, props]);

  const onSubmit = (data: any, e: any) => {
    console.log("Data to be submitted", data, e);
    const defaults = props.value || {};
    const overrides = Object.fromEntries(
      props.spec
        .filter(spec => data[spec.name] !== undefined)
        .map(spec => [spec.name, convertFromInputValue(spec, data)]),
    );
    const values = { ...defaults, ...overrides };
    AckLogger.info("Values for [default:override:final] to be submitted", defaults, overrides, values);
    if (props.onSubmit) props.onSubmit(values);
  };

  return (
    <div className={props.className || ""}>
      <form className="form" onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset disabled={props.mode === FormModes.READ}>
          {renderForm(props.spec, props.validators || {}, form)}
          {props.mode == FormModes.WRITE && <div className="form-actions">
            <div className="box">
              <button className="button is-primary" type="submit">Submit</button>
            </div>
          </div>}
        </fieldset>
      </form>
    </div>
  );
};

/** AckMultiModelForm component. */
export const AckMultiModelForm: FunctionComponent<AckMultiModelFormProps> = (props) => {
  const container = useRef(null as HTMLDivElement | null);
  const formData = [] as object[];
  const formSubmit = [] as (() => void)[];

  const InnerForm: FunctionComponent<AckModelFormProps> = (innerProps: AckModelFormProps) => {
    const form = useForm();

    useEffect(() => {
      const entries = innerProps.spec.map(spec => [spec.name, convertToInputValue(spec, innerProps.value)]);
      const values = Object.fromEntries(entries);
      form.reset(values);
      formSubmit.push(form.handleSubmit(onSubmit));
    }, [form.reset, props]);

    const onSubmit = (data: object) => {
      const defaults = innerProps.value || {};
      const overrides = Object.fromEntries(
        innerProps.spec
          .filter(spec => data[spec.name] !== undefined)
          .map(spec => [spec.name, convertFromInputValue(spec, data)]),
      );
      const values = { ...defaults, ...overrides };
      AckLogger.info("Values for [default:override:final] to be submitted", defaults, overrides, values);
      formData.push(values);
      // TODO: Major hack to make inner forms work
      if (formData.length === props.size && props.onSubmit) props.onSubmit(formData);
    };

    return (
      <div className="box mt-4 mb-4">
        <form className="form" onSubmit={form.handleSubmit(onSubmit)}>
          <fieldset disabled={innerProps.mode === FormModes.READ}>
            {renderForm(innerProps.spec, innerProps.validators || {}, form)}
          </fieldset>
        </form>
        <br/>
      </div>
    );
  };

  const onMultiSubmit = () => formSubmit.forEach(block => block());

  const innerProps: AckModelFormProps[] = [...Array(props.size)].map((_, index) => {
    return {
      spec: props.spec,
      mode: props.mode,
      value: props?.values ? props?.values[index] : undefined,
      className: props.className,
      validators: props.validators,
    };
  });

  return (
    <div className={props.className || ""} ref={container}>
      {innerProps.map((inner, index) => <InnerForm key={"inner-form-" + index} {...inner}/>)}
      {props.mode == FormModes.WRITE && <div className="form-actions">
        <div className="box">
          <button className="button is-primary" type="button" onClick={onMultiSubmit}>Submit</button>
        </div>
      </div>}
    </div>
  );
};
